// Configuración multi-tenant para instituciones
// Cada institución tiene su propia identidad visual, logo y metadatos.
// La detección se hace por variable de entorno REACT_APP_INSTITUTION.
// Si no se define, se usa 'liceo' como valor por defecto.

const institutions = {
  liceo: {
    id: 'liceo',
    name: 'Liceo Salazar y Herrera',
    shortName: 'Liceo S&H',
    pageTitle: 'Publicaciones Virtuales — Liceo Salazar y Herrera',
    description: 'Explora las publicaciones digitales interactivas del Liceo Salazar y Herrera',
    logo: '/logo/logo-liceo.svg',
    // Rutas al logo alternativo (si existe)
    logoAlt: '/logo/logo2.svg',
    // Colores institucionales
    colors: {
      // Color principal institucional
      primary: '#e20e17',
      primaryDark: '#c20c14',
      primaryLight: '#f43f5e',
      // Gradiente del header
      headerGradientStart: '#c20c14',
      headerGradientMid: '#e20e17',
      headerGradientEnd: '#c20c14',
      // Borde inferior del header
      headerBorder: 'rgba(194,12,20,0.3)',
      // Sombra del header
      headerShadowPrimary: 'rgba(194,12,20,0.25)',
      headerShadowSecondary: 'rgba(194,12,20,0.15)',
      // Dark mode del header
      headerDarkGradientStart: '#a00b12',
      headerDarkGradientMid: '#c20c14',
      headerDarkGradientEnd: '#a00b12',
      headerDarkBorder: 'rgba(160,11,18,0.5)',
      // Colores de acento para tarjetas y títulos
      accentFrom: '#dc2626',  // red-600 (Tailwind)
      accentTo: '#ef4444',    // red-500 (Tailwind)
      // Colores de gradiente para tarjetas de categoría
      cardGradientFrom: '#ef4444',  // red-500
      cardGradientVia: '#dc2626',   // red-600
      cardGradientTo: '#b91c1c',    // red-700
      // Color del badge en tarjetas
      badgeText: '#b91c1c',
      // Color de acento para los CTAs
      ctaText: '#dc2626',
      ctaTextHover: '#b91c1c',
    },
    // Clases Tailwind específicas (para ahorrar repetición en JSX)
    tailwind: {
      titleGradient: 'from-red-700 to-red-500',
      cardHeaderGradient: 'from-red-500 via-red-600 to-red-700',
      badgeText: 'text-red-700',
      ctaText: 'text-red-600',
      ctaHover: 'group-hover:text-red-700',
      buttonGradient: 'from-red-600 to-red-700',
      buttonHoverGradient: 'hover:from-red-700 hover:to-red-800',
      infoBg: 'from-red-50 to-orange-50',
      infoBorder: 'border-red-200',
      infoIconGradient: 'from-red-500 to-red-700',
      stepBadgeBg: 'bg-red-600',
      codeBorder: 'border-red-200',
    },
    // Basename del router (se usa en homepage de package.json y Router basename)
    basePath: '/centro-publicaciones',
    // Homepage para despliegue
    homepage: '/centro-publicaciones',
  },

  iush: {
    id: 'iush',
    name: 'Institución Universitaria Salazar y Herrera',
    shortName: 'IUSH',
    pageTitle: 'Publicaciones Virtuales — IUSH',
    description: 'Explora las publicaciones digitales interactivas de la IUSH',
    logo: '/logo/logo-iush.svg',
    logoAlt: '/logo/logo2.svg',
    // Colores institucionales de la IUSH
    colors: {
      // Azul institucional principal
      primary: '#163073',
      primaryDark: '#0f1f4d',
      primaryLight: '#1e4291',
      // Gradiente del header: azul institucional → índigo → violeta institucional
      headerGradientStart: '#163073',   // azul institucional
      headerGradientMid: '#3b2877',     // tono medio índigo
      headerGradientEnd: '#601082',     // violeta institucional
      headerBorder: 'rgba(22,48,115,0.3)',
      headerShadowPrimary: 'rgba(22,48,115,0.25)',
      headerShadowSecondary: 'rgba(22,48,115,0.15)',
      // Dark mode del header
      headerDarkGradientStart: '#0f1f4d',
      headerDarkGradientMid: '#2b1a5a',
      headerDarkGradientEnd: '#4a0a64',
      headerDarkBorder: 'rgba(15,31,77,0.5)',
      // Colores de acento para tarjetas y títulos
      accentFrom: '#163073',  // azul institucional
      accentTo: '#601082',    // violeta institucional
      // Colores de gradiente para tarjetas de categoría
      cardGradientFrom: '#1e4291',  // azul claro
      cardGradientVia: '#3b2877',   // índigo medio
      cardGradientTo: '#601082',    // violeta institucional
      // Color del badge en tarjetas
      badgeText: '#601082',   // violeta institucional
      // Color de acento para los CTAs
      ctaText: '#163073',     // azul institucional
      ctaTextHover: '#0f1f4d', // azul oscuro
    },
    tailwind: {
      titleGradient: 'from-blue-600 to-purple-600',
      cardHeaderGradient: 'from-blue-500 via-indigo-500 to-purple-600',
      badgeText: 'text-purple-700',
      ctaText: 'text-blue-600',
      ctaHover: 'group-hover:text-purple-600',
      buttonGradient: 'from-blue-600 to-purple-600',
      buttonHoverGradient: 'hover:from-blue-700 hover:to-purple-700',
      infoBg: 'from-blue-50 to-purple-50',
      infoBorder: 'border-blue-200',
      infoIconGradient: 'from-blue-500 to-purple-600',
      stepBadgeBg: 'bg-blue-600',
      codeBorder: 'border-blue-200',
    },
    basePath: '/centro-publicaciones',
    homepage: '/centro-publicaciones',
  },
};

// --- Detección de institución ---

/**
 * Obtiene el ID de la institución actual.
 * Se determina por la variable de entorno REACT_APP_INSTITUTION.
 * Si no se define, se usa 'liceo' como default.
 */
const getInstitutionId = () => {
  const envId = process.env.REACT_APP_INSTITUTION || 'liceo';
  return institutions[envId] ? envId : 'liceo';
};

/**
 * Devuelve la configuración completa de la institución actual.
 */
export const getInstitution = () => {
  return institutions[getInstitutionId()];
};

/**
 * Devuelve los colores de la institución actual.
 */
export const getInstitutionColors = () => {
  return getInstitution().colors;
};

/**
 * Devuelve las clases tailwind de la institución actual.
 */
export const getInstitutionTailwind = () => {
  return getInstitution().tailwind;
};

/**
 * Devuelve el ID de la institución actual.
 */
export const getInstitutionId_ = () => getInstitutionId();

// Exportar todo para uso avanzado
export { institutions };
export default institutions;
